import { useMemo, useState } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import { useHandovers, useShiftLocations, useShifts, useUpsertHandover } from '../api/hooks';
import type { OfficeHandover, ShiftLocation } from '../api/types';
import { useLang, useT } from '../i18n/LangProvider';
import { toast } from '../ui/toast';
import { Query } from '../ui/atoms';
import { Icon } from '../ui/Icon';
import { RichTextEditor, isHtml, sanitizeHtml } from '../ui/RichTextEditor';

const LOC_COLOR: Record<string, string> = {
  uff: '#e79aa6', vat: '#93cf8f', col: '#5bb8e6', nav: '#f2a63d', home: '#c3ace0', csam: '#9ad0c0', borgh: '#e6c34a',
};
const locColor = (k: string) => LOC_COLOR[k] ?? '#c9c9c9';
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDay = (s: string, loc = 'it-IT') => new Date(s).toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' });
const fmtShort = (s: string, loc = 'it-IT') => new Date(s).toLocaleDateString(loc, { day: '2-digit', month: 'short' });
const minusDays = (s: string, n: number) => { const d = new Date(`${s}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - n); return iso(d); };

function NoteBody({ body }: { body: string }) {
  return isHtml(body)
    ? <div className="md-body" style={{ fontSize: 13.5 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }} />
    : <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{body}</div>;
}

function OfficeCard({ office, day, notes, canWrite, peopleCount }: {
  office: ShiftLocation; day: string; notes: OfficeHandover[]; canWrite: boolean; peopleCount: number;
}) {
  const { lang, t } = useLang();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const upsert = useUpsertHandover();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const today = notes.find((n) => n.date.slice(0, 10) === day);
  const prev = notes.find((n) => n.date.slice(0, 10) < day);
  const history = notes.filter((n) => n.date.slice(0, 10) !== day);

  async function save() {
    if (!draft.replace(/<[^>]+>/g, '').trim()) { setEditing(false); return; }
    try {
      await upsert.mutateAsync({ officeKey: office.key, date: day, body: draft });
      setEditing(false); setDraft('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('Salvataggio non riuscito'));
    }
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="spaced" style={{ marginBottom: 10 }}>
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <span style={{ background: locColor(office.key), color: '#222', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '4px 9px' }}>{office.short ?? office.key.toUpperCase()}</span>
          <div>
            <strong>{office.label}</strong>
            <div className="muted" style={{ fontSize: 12 }}>{t('{n} in turno', { n: peopleCount })} · {fmtDay(day, loc)}</div>
          </div>
        </div>
        {canWrite && !editing && (
          <button className="btn btn--sm btn--brand" onClick={() => { setEditing(true); setDraft(today?.body ?? ''); }}>
            <Icon name="edit" size={12} /> {today ? t('Modifica') : t('Scrivi consegna')}
          </button>
        )}
      </div>

      {prev && !editing && (
        <div style={{ borderLeft: '3px solid var(--t-orange)', background: 'var(--bg-sunk,#f8f8f8)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 4 }}>
            {t('Dal turno precedente')} · {fmtDay(prev.date, loc)}
          </div>
          <NoteBody body={prev.body} />
          {prev.authorName && <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>— {prev.authorName}</div>}
        </div>
      )}

      {editing ? (
        <div>
          <RichTextEditor value={draft} onChange={setDraft} minHeight={160} placeholder={t('Cosa deve sapere chi arriva domani…')} />
          <div className="cluster" style={{ justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn--sm" onClick={() => { setEditing(false); setDraft(''); }}>{t('Annulla')}</button>
            <button className="btn btn--sm btn--brand" onClick={() => void save()} disabled={upsert.isPending}>{t('Salva consegna')}</button>
          </div>
        </div>
      ) : today ? (
        <div>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 4 }}>{t('Consegna di')} {fmtDay(day, loc)}</div>
          <NoteBody body={today.body} />
          {today.authorName && <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>— {today.authorName}</div>}
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 13 }}>
          {canWrite ? t('Nessuna consegna per questa giornata — scrivila a fine turno.') : t('Nessuna consegna per questa giornata.')}
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--line,#eee)', paddingTop: 10 }}>
          <button className="btn btn--sm" onClick={() => setShowHistory((s) => !s)}>
            <Icon name={showHistory ? 'chevD' : 'chevR'} size={12} /> {t('Storico')} ({history.length})
          </button>
          {showHistory && (
            <div className="col" style={{ gap: 8, marginTop: 10 }}>
              {history.map((n) => (
                <div key={n.id} style={{ background: 'var(--bg-sunk,#f8f8f8)', borderRadius: 8, padding: '8px 10px' }}>
                  <div className="spaced" style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{fmtShort(n.date, loc)}</span>
                    {n.authorName && <span className="muted" style={{ fontSize: 11.5 }}>{n.authorName}</span>}
                  </div>
                  <NoteBody body={n.body} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function HandoverScreen() {
  const t = useT();
  const auth = useAuth();
  const officesQ = useShiftLocations();
  const shiftsQ = useShifts();
  const [day, setDay] = useState(() => iso(new Date()));
  const [showAll, setShowAll] = useState(false);

  // One month of history, ending on the selected day.
  const handoversQ = useHandovers({ from: minusDays(day, 31), to: day });

  const offices = officesQ.data ?? [];
  const notes = handoversQ.data ?? [];
  const privileged = auth.hasRole(Roles.ADMIN) || auth.hasRole(Roles.TEAM_LEAD);

  /** Offices staffed on the selected day, and which of them are mine. */
  const { peopleByOffice, myOffices } = useMemo(() => {
    const counts: Record<string, number> = {};
    const mine = new Set<string>();
    for (const s of shiftsQ.data ?? []) {
      if (s.date.slice(0, 10) !== day) continue;
      const segs = (s.segments?.length ? s.segments : (s.locationKey ? [{ loc: s.locationKey }] : [])).filter((x) => x?.loc);
      for (const k of new Set(segs.map((x) => x!.loc as string))) {
        counts[k] = (counts[k] ?? 0) + 1;
        if (s.employeeId === auth.sub) mine.add(k);
      }
    }
    return { peopleByOffice: counts, myOffices: mine };
  }, [shiftsQ.data, day, auth.sub]);

  const notesByOffice = useMemo(() => {
    const m: Record<string, OfficeHandover[]> = {};
    for (const n of notes) (m[n.officeKey] ??= []).push(n);
    for (const k of Object.keys(m)) m[k].sort((a, b) => b.date.localeCompare(a.date));
    return m;
  }, [notes]);

  // Show what matters: my offices (or, for managers, the staffed ones). "Tutte" reveals the rest.
  const relevant = offices.filter((o) => (privileged ? (peopleByOffice[o.key] ?? 0) > 0 : myOffices.has(o.key)));
  const visible = showAll ? offices : (relevant.length > 0 ? relevant : []);

  return (
    <div style={{ padding: 24 }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="h1">{t('Consegne di fine giornata')}</h1>
          <div className="page-head__sub">
            {privileged
              ? t('Sedi con personale in turno nella giornata scelta. Storico dell’ultimo mese incluso.')
              : t('Le sedi in cui sei in turno. Puoi scrivere la consegna solo per quelle.')}
          </div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input type="date" className="input" value={day} onChange={(e) => setDay(e.target.value)} style={{ width: 160 }} />
          <button className="btn btn--sm" onClick={() => setDay(iso(new Date()))}>{t('Oggi')}</button>
          <button className={`btn btn--sm ${showAll ? 'btn--primary' : ''}`} onClick={() => setShowAll((s) => !s)}>
            {showAll ? t('Solo rilevanti') : t('Tutte le sedi')}
          </button>
        </div>
      </div>

      <Query query={officesQ} empty={{ test: (d) => d.length === 0, title: t('Nessuna sede'), hint: t('Crea gli uffici dalla Dashboard Admin.') }}>
        {() => (
          visible.length === 0 ? (
            <div className="card" style={{ padding: 24, maxWidth: 560 }}>
              <h2 className="h2" style={{ marginBottom: 6 }}>{t('Nessuna sede da mostrare')}</h2>
              <p className="muted" style={{ margin: '0 0 12px' }}>
                {privileged
                  ? t('Nessuno risulta in turno in questa giornata.')
                  : t('Non risulti in turno in nessuna sede in questa giornata: non puoi scrivere consegne.')}
              </p>
              <button className="btn btn--sm" onClick={() => setShowAll(true)}>{t('Mostra comunque tutte le sedi')}</button>
            </div>
          ) : (
            <div className="grid grid-2" style={{ gap: 16 }}>
              {visible.map((o) => (
                <OfficeCard key={o.key} office={o} day={day}
                  notes={notesByOffice[o.key] ?? []}
                  canWrite={privileged || myOffices.has(o.key)}
                  peopleCount={peopleByOffice[o.key] ?? 0} />
              ))}
            </div>
          )
        )}
      </Query>
    </div>
  );
}
